import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Grid, Tabs, Tab, Chip, Button,
  IconButton, TextField, InputAdornment, Select, MenuItem, FormControl,
  InputLabel, Dialog, DialogTitle, DialogContent, DialogActions,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, LinearProgress, Tooltip, Alert, Badge, Divider, List, ListItem,
  ListItemIcon, ListItemText, Collapse, CircularProgress, Avatar
} from '@mui/material';
import {
  Search as SearchIcon,
  PlayArrow as PlayIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Security as SecurityIcon,
  Computer as ComputerIcon,
  Router as RouterIcon,
  Storage as StorageIcon,
  Dns as DnsIcon,
  Memory as MemoryIcon,
  Videocam as CameraIcon,
  Language as WebIcon,
  Shield as ShieldIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  HourglassEmpty as PendingIcon,
  Settings as SettingsIcon,
  Analytics as AnalyticsIcon,
  BugReport as BugIcon,
  Edit as EditIcon,
  Verified as VerifiedIcon
} from '@mui/icons-material';
import api from '../../services/api/client';
import { useI18n } from '../../i18n';
import { usePageTitle } from '../../hooks/usePageTitle';

// Category colors
const CATEGORY_COLORS = {
  server: '#2196F3',
  workstation: '#9C27B0',
  network: '#00BCD4',
  security: '#4CAF50',
  iot: '#FF9800',
  ot_ics: '#F44336',
  mobile: '#E91E63',
  cloud: '#03A9F4',
  container: '#009688',
  database: '#795548',
  web_application: '#673AB7',
  printer: '#607D8B',
  storage: '#8BC34A',
  voip: '#FF5722',
  other: '#9E9E9E'
};

// Category icons
const CATEGORY_ICONS = {
  server: <ComputerIcon />,
  workstation: <ComputerIcon />,
  network: <RouterIcon />,
  security: <ShieldIcon />,
  iot: <SettingsIcon />,
  ot_ics: <MemoryIcon />,
  mobile: <ComputerIcon />,
  cloud: <StorageIcon />,
  container: <DnsIcon />,
  database: <StorageIcon />,
  web_application: <WebIcon />,
  printer: <SettingsIcon />,
  storage: <StorageIcon />,
  voip: <RouterIcon />,
  other: <SettingsIcon />
};

const CRITICALITY_COLORS = {
  low: '#4CAF50',
  medium: '#FF9800',
  high: '#F44336',
  critical: '#9C27B0'
};

// French translations for equipment types
const EQUIPMENT_TYPE_TRANSLATIONS = {
  // Display names
  display_names: {
    'Windows Server': 'Serveur Windows',
    'Linux Server': 'Serveur Linux',
    'Active Directory Domain Controller': 'Active Directory Domain Controller',
    'Cisco Router': 'Routeur Cisco',
    'Firewall / UTM': 'Pare-feu / UTM',
    'Siemens PLC': 'Automate Siemens',
    'SCADA / HMI': 'SCADA / IHM',
    'IP Camera / NVR': 'IP Camera / NVR',
    'Web Application': 'Application Web',
    'MySQL / MariaDB': 'MySQL / MariaDB',
    'Microsoft SQL Server': 'Microsoft SQL Server'
  },
  // Descriptions
  descriptions: {
    'Microsoft Windows Server (2012, 2016, 2019, 2022)': 'Serveur Microsoft Windows (2012, 2016, 2019, 2022)',
    'Linux-based server (Ubuntu, CentOS, Debian, RHEL)': 'Serveur Linux (Ubuntu, CentOS, Debian, RHEL)',
    'Windows Active Directory Domain Controller': 'Windows Active Directory Domain Controller',
    'Cisco IOS/IOS-XE Router': 'Routeur Cisco IOS/IOS-XE',
    'Network firewall or UTM appliance': 'Network firewall or UTM appliance',
    'Siemens S7 Programmable Logic Controller': 'Automate programmable Siemens S7',
    'SCADA system or HMI interface': 'SCADA system or HMI interface',
    'IP surveillance camera or network video recorder': 'IP surveillance camera or network video recorder',
    'Web application or API service': 'Application web ou service API',
    'MySQL or MariaDB database server': 'MySQL or MariaDB database server',
    'Microsoft SQL Server database': 'Microsoft SQL Server database'
  },
  // Categories
  categories: {
    'server': 'Serveur',
    'workstation': 'Poste de travail',
    'network': 'Network',
    'security': 'Security',
    'iot': 'IoT',
    'ot_ics': 'OT/ICS',
    'mobile': 'Mobile',
    'cloud': 'Cloud',
    'container': 'Conteneur',
    'database': 'Database',
    'web_application': 'Application Web',
    'printer': 'Imprimante',
    'storage': 'Stockage',
    'voip': 'VoIP',
    'other': 'Autre'
  }
};

// Helper function to get translated text
const getTranslatedText = (type, field) => {
  if (field === 'display_name') {
    return EQUIPMENT_TYPE_TRANSLATIONS.display_names[type.display_name] || type.display_name;
  }
  if (field === 'description') {
    return EQUIPMENT_TYPE_TRANSLATIONS.descriptions[type.description] || type.description;
  }
  if (field === 'category') {
    return EQUIPMENT_TYPE_TRANSLATIONS.categories[type.category] || type.category;
  }
  return type[field];
};

const STATUS_COLORS = {
  planned: '#9E9E9E',
  in_progress: '#2196F3',
  success: '#4CAF50',
  partial: '#FF9800',
  failed: '#F44336',
  blocked: '#795548',
  detected: '#E91E63'
};

// Tab Panel Component
function TabPanel({ children, value, index, ...props }) {
  return (
    <div role="tabpanel" hidden={value !== index} {...props}>
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  );
}

// Equipment Type Card Component
function EquipmentTypeCard({ type, onSelect, selected }) {
  const { t } = useI18n();
  return (
    <Card
      sx={{
        cursor: 'pointer',
        border: selected ? `3px solid ${type.color || CATEGORY_COLORS[type.category]}` : '1px solid #e0e0e0',
        transition: 'all 0.2s',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: 3
        }
      }}
      onClick={() => onSelect(type)}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Avatar sx={{ bgcolor: type.color || CATEGORY_COLORS[type.category], width: 40, height: 40 }}>
            {CATEGORY_ICONS[type.category] || <ComputerIcon />}
          </Avatar>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Tooltip title={getTranslatedText(type, 'display_name')} arrow placement="top">
              <Typography variant="subtitle1" fontWeight="bold" noWrap>
                {getTranslatedText(type, 'display_name')}
              </Typography>
            </Tooltip>
            <Typography variant="caption" color="text.secondary">
              {getTranslatedText(type, 'category')}
            </Typography>
          </Box>
        </Box>

        <Tooltip title={getTranslatedText(type, 'description')} arrow placement="top">
          <Typography variant="body2" color="text.secondary" sx={{ height: 40, overflow: 'hidden', mb: 1 }}>
            {getTranslatedText(type, 'description')}
          </Typography>
        </Tooltip>

        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
          <Chip
            label={type.default_criticality}
            size="small"
            sx={{
              bgcolor: CRITICALITY_COLORS[type.default_criticality],
              color: 'white',
              fontSize: '0.7rem'
            }}
          />
        </Box>

        {type.common_ports && type.common_ports.length > 0 && (
          <Typography variant="caption" color="text.secondary" display="block">
            {t('scans.ports')}: {type.common_ports.slice(0, 5).join(', ')}{type.common_ports.length > 5 ? '...' : ''}
          </Typography>
        )}

        {type.recommended_attacks && type.recommended_attacks.length > 0 && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
              {t('ad.attacks')}:
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {type.recommended_attacks.slice(0, 3).map((attack, i) => (
                <Chip
                  key={i}
                  label={typeof attack === 'string' ? attack.replace('_', ' ') : attack.name}
                  size="small"
                  variant="outlined"
                  color="error"
                  sx={{ fontSize: '0.65rem' }}
                />
              ))}
              {type.recommended_attacks.length > 3 && (
                <Chip
                  label={`+${type.recommended_attacks.length - 3}`}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.65rem' }}
                />
              )}
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

// Equipment Instance Row Component
function EquipmentInstanceRow({ instance, onValidate, onDelete, onAttack }) {
  const [expanded, setExpanded] = useState(false);
  const { t } = useI18n();

  return (
    <>
      <TableRow
        sx={{ '&:hover': { bgcolor: 'action.hover' }, cursor: 'pointer' }}
        onClick={() => setExpanded(!expanded)}
      >
        <TableCell>
          <IconButton size="small">
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar sx={{ bgcolor: CATEGORY_COLORS[instance.category], width: 32, height: 32 }}>
              {CATEGORY_ICONS[instance.category] || <ComputerIcon fontSize="small" />}
            </Avatar>
            <Box>
              <Typography variant="body2" fontWeight="bold">
                {instance.name || instance.ip_address || t('common.unknown')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {instance.hostname}
              </Typography>
            </Box>
          </Box>
        </TableCell>
        <TableCell>{instance.ip_address}</TableCell>
        <TableCell>
          <Chip
            label={instance.category}
            size="small"
            sx={{ bgcolor: CATEGORY_COLORS[instance.category], color: 'white' }}
          />
        </TableCell>
        <TableCell>
          <Chip
            label={instance.criticality}
            size="small"
            sx={{ bgcolor: CRITICALITY_COLORS[instance.criticality], color: 'white' }}
          />
        </TableCell>
        <TableCell>
          {instance.is_validated ? (
            <Chip icon={<VerifiedIcon />} label={t('apt.validated')} size="small" color="success" />
          ) : (
            <Chip label={t('apt.status.pending')} size="small" variant="outlined" />
          )}
        </TableCell>
        <TableCell>
          {instance.detection_confidence && (
            <LinearProgress
              variant="determinate"
              value={instance.detection_confidence * 100}
              sx={{ width: 60, mr: 1, display: 'inline-flex' }}
            />
          )}
          {Math.round((instance.detection_confidence || 0) * 100)}%
        </TableCell>
        <TableCell onClick={(e) => e.stopPropagation()}>
          <Tooltip title={t('equipmentTypes.launchAttack')}>
            <IconButton size="small" color="error" onClick={() => onAttack(instance)}>
              <BugIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('equipmentTypes.validateDetection')}>
            <IconButton size="small" color="success" onClick={() => onValidate(instance)}>
              <VerifiedIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('common.delete')}>
            <IconButton size="small" color="error" onClick={() => onDelete(instance)}>
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={8} sx={{ py: 0 }}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box sx={{ py: 2, px: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" gutterBottom>Details</Typography>
                  <Typography variant="body2">OS : {instance.os_name} {instance.os_version}</Typography>
                  <Typography variant="body2">Fabricant : {instance.vendor || 'N/A'}</Typography>
                  <Typography variant="body2">Model: {instance.model || 'N/A'}</Typography>
                  <Typography variant="body2">Detection: {instance.detection_method}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" gutterBottom>Ports Ouverts</Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {instance.open_ports?.map((port, i) => (
                      <Chip key={i} label={port} size="small" variant="outlined" />
                    )) || <Typography variant="body2">No ports detected</Typography>}
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

// Main Dashboard Component
export default function EquipmentDashboard() {
  const { t } = useI18n();
  usePageTitle(t('equipment.title'));
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [equipmentTypes, setEquipmentTypes] = useState([]);
  const [equipmentInstances, setEquipmentInstances] = useState([]);
  const [attacks, setAttacks] = useState([]);
  const [stats, setStats] = useState(null);
  const [categories, setCategories] = useState([]);
  const [selectedType, setSelectedType] = useState(null);
  const [filterCategory, setFilterCategory] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [autoDetectDialog, setAutoDetectDialog] = useState(false);
  const [selectedScanId, setSelectedScanId] = useState('');
  const [attackDialog, setAttackDialog] = useState(false);
  const [selectedInstance, setSelectedInstance] = useState(null);
  const [message, setMessage] = useState({ text: '', severity: 'info' });

  // Fetch equipment types
  const fetchEquipmentTypes = useCallback(async () => {
    try {
      const response = await api.get('/equipment/types', { params: { category: filterCategory || undefined } });
      setEquipmentTypes(response.data);
    } catch (error) {
      console.error('Error fetching equipment types:', error);
    }
  }, [filterCategory]);

  // Fetch equipment instances
  const fetchEquipmentInstances = useCallback(async () => {
    try {
      const response = await api.get('/equipment/instances', {
        params: {
          category: filterCategory || undefined,
          equipment_type_id: selectedType?.id || undefined
        }
      });
      setEquipmentInstances(response.data);
    } catch (error) {
      console.error('Error fetching equipment instances:', error);
    }
  }, [filterCategory, selectedType]);

  // Fetch attacks
  const fetchAttacks = useCallback(async () => {
    try {
      const response = await api.get('/equipment/attacks');
      setAttacks(response.data);
    } catch (error) {
      console.error('Error fetching attacks:', error);
    }
  }, []);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await api.get('/equipment/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, []);

  // Fetch categories
  const fetchCategories = useCallback(async () => {
    try {
      const response = await api.get('/equipment/categories');
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  }, []);

  // Load data
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchEquipmentTypes(),
        fetchEquipmentInstances(),
        fetchAttacks(),
        fetchStats(),
        fetchCategories()
      ]);
    } finally {
      setLoading(false);
    }
  }, [fetchEquipmentTypes, fetchEquipmentInstances, fetchAttacks, fetchStats, fetchCategories]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    fetchEquipmentTypes();
    fetchEquipmentInstances();
  }, [filterCategory, selectedType, fetchEquipmentTypes, fetchEquipmentInstances]);

  // Validate instance
  const handleValidateInstance = async (instance) => {
    try {
      await api.patch(`/equipment/instances/${instance.id}/validate`);
      setMessage({ text: t('success.updated'), severity: 'success' });
      fetchEquipmentInstances();
    } catch (error) {
      console.error('Error validating instance:', error);
      setMessage({ text: t('equipmentTypes.validationError'), severity: 'error' });
    }
  };

  // Delete instance
  const handleDeleteInstance = async (instance) => {
    if (!window.confirm(t('equipmentTypes.deleteConfirm'))) return;
    try {
      await api.delete(`/equipment/instances/${instance.id}`);
      setMessage({ text: t('equipmentTypes.deleted'), severity: 'success' });
      fetchEquipmentInstances();
    } catch (error) {
      console.error('Error deleting instance:', error);
      setMessage({ text: t('equipmentTypes.deleteError'), severity: 'error' });
    }
  };

  // Open attack dialog
  const handleOpenAttackDialog = (instance) => {
    setSelectedInstance(instance);
    setAttackDialog(true);
  };

  // Launch attack
  const handleLaunchAttack = async (attackName) => {
    if (!selectedInstance) return;
    try {
      await api.post('/equipment/attacks', {
        equipment_instance_id: selectedInstance.id,
        name: attackName,
        attack_vector: 'network'
      });
      setMessage({ text: 'Attack launched successfully', severity: 'success' });
      setAttackDialog(false);
      fetchAttacks();
    } catch (error) {
      console.error('Error launching attack:', error);
      setMessage({ text: 'Erreur lors du lancement de l\'attaque', severity: 'error' });
    }
  };

  // Auto-detect from scan
  const handleAutoDetect = async () => {
    if (!selectedScanId) return;
    try {
      setLoading(true);
      const response = await api.post('/equipment/auto-detect', { scan_id: selectedScanId });
      setMessage({ text: `${response.data.detected_count} equipment detected`, severity: 'success' });
      setAutoDetectDialog(false);
      fetchEquipmentInstances();
    } catch (error) {
      console.error('Error auto-detecting equipment:', error);
      setMessage({ text: 'Error during automatic detection', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Filter types by search
  const filteredTypes = equipmentTypes.filter(type =>
    type.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    type.display_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    type.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Box sx={{ p: 3 }}>
      {/* Message Alert */}
      {message.text && (
        <Alert
          severity={message.severity}
          onClose={() => setMessage({ text: '', severity: 'info' })}
          sx={{ mb: 2 }}
        >
          {message.text}
        </Alert>
      )}

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
          <Typography variant="h4" gutterBottom sx={{ wordBreak: 'break-word' }}>
            {t('equipment.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
            {t('equipment.subtitle')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexShrink: 0, flexWrap: 'wrap' }}>
          <Button
            startIcon={<AddIcon />}
            variant="outlined"
            onClick={() => setAutoDetectDialog(true)}
          >
            {t('equipment.autoDetect')}
          </Button>
          <Button
            startIcon={<RefreshIcon />}
            variant="contained"
            onClick={loadData}
          >
            {t('equipment.refresh')}
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Tooltip
              title={
                <Box sx={{ p: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>{t('equipment.totalEquipment')}</Typography>
                  <Typography variant="body2">{t('equipment.totalEquipmentDesc')}</Typography>
                </Box>
              }
              arrow
              placement="top"
            >
              <Card sx={{ cursor: 'help' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar sx={{ bgcolor: '#2196F3' }}>
                      <ComputerIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="h4">{stats.total_equipment}</Typography>
                      <Typography variant="body2" color="text.secondary">{t('equipment.equipmentLabel')}</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Tooltip>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Tooltip
              title={
                <Box sx={{ p: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>Total Attaques</Typography>
                  <Typography variant="body2">Total number of attack techniques configured and executable on equipment. Each type of equipment has specific attack vectors.</Typography>
                </Box>
              }
              arrow
              placement="top"
            >
              <Card sx={{ cursor: 'help' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar sx={{ bgcolor: '#F44336' }}>
                      <BugIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="h4">{stats.total_attacks}</Typography>
                      <Typography variant="body2" color="text.secondary">{t('equipment.attacksLabel')}</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Tooltip>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Tooltip
              title={
                <Box sx={{ p: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>Successful attacks</Typography>
                  <Typography variant="body2">Attacks that resulted in successful exploitation of the target. Indicates confirmed vulnerabilities and accesses obtained during penetration tests.</Typography>
                </Box>
              }
              arrow
              placement="top"
            >
              <Card sx={{ cursor: 'help' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar sx={{ bgcolor: '#4CAF50' }}>
                      <SuccessIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="h4">{stats.successful_attacks}</Typography>
                      <Typography variant="body2" color="text.secondary">{t('equipment.successfulAttacksLabel')}</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Tooltip>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Tooltip
              title={
                <Box sx={{ p: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>Attaques en cours</Typography>
                  <Typography variant="body2">Attacks currently running. These tests are executing on target equipment and waiting for completion.</Typography>
                </Box>
              }
              arrow
              placement="top"
            >
              <Card sx={{ cursor: 'help' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar sx={{ bgcolor: '#FF9800' }}>
                      <PendingIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="h4">{stats.in_progress_attacks}</Typography>
                      <Typography variant="body2" color="text.secondary">{t('equipment.ongoingAttacksLabel')}</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Tooltip>
          </Grid>
        </Grid>
      )}

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2, overflow: 'visible' }}>
        <Tabs value={tab} onChange={(e, v) => setTab(v)} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile>
          <Tab label={t('equipment.typesTab')} icon={<ComputerIcon />} iconPosition="start" sx={{ textTransform: 'none', minWidth: 'auto', px: 2 }} />
          <Tab
            label={
              <Badge badgeContent={equipmentInstances.length} color="primary">
                {t('equipment.detectedTab')}
              </Badge>
            }
            icon={<AnalyticsIcon />}
            iconPosition="start"
            sx={{ textTransform: 'none', minWidth: 'auto', px: 2 }}
          />
          <Tab
            label={
              <Badge badgeContent={attacks.length} color="error">
                {t('equipment.attacksTab')}
              </Badge>
            }
            icon={<BugIcon />}
            iconPosition="start"
            sx={{ textTransform: 'none', minWidth: 'auto', px: 2 }}
          />
        </Tabs>
      </Box>

      {/* Tab 0: Equipment Types */}
      <TabPanel value={tab} index={0}>
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder={t('equipment.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>
            }}
            sx={{ width: 300 }}
          />
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>{t('equipment.category')}</InputLabel>
            <Select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              label={t('equipment.category')}
            >
              <MenuItem value="">{t('equipment.allCategories')}</MenuItem>
              {categories.map(cat => (
                <MenuItem key={cat.value} value={cat.value}>{cat.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Grid container spacing={2}>
          {filteredTypes.map(type => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={type.id}>
              <EquipmentTypeCard
                type={type}
                onSelect={setSelectedType}
                selected={selectedType?.id === type.id}
              />
            </Grid>
          ))}
        </Grid>
      </TabPanel>

      {/* Tab 1: Equipment Instances */}
      <TabPanel value={tab} index={1}>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width={50}></TableCell>
                <TableCell>Equipment</TableCell>
                <TableCell>IP</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Criticality</TableCell>
                <TableCell>Statut</TableCell>
                <TableCell>Confiance</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {equipmentInstances.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography color="text.secondary" sx={{ py: 4 }}>
                      No equipment detected. Launch automatic detection from a scan.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                equipmentInstances.map(instance => (
                  <EquipmentInstanceRow
                    key={instance.id}
                    instance={instance}
                    onValidate={handleValidateInstance}
                    onDelete={handleDeleteInstance}
                    onAttack={handleOpenAttackDialog}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* Tab 2: Attacks */}
      <TabPanel value={tab} index={2}>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Attaque</TableCell>
                <TableCell>Cible</TableCell>
                <TableCell>Vecteur</TableCell>
                <TableCell>Statut</TableCell>
                <TableCell>Complexity</TableCell>
                <TableCell>Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {attacks.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography color="text.secondary" sx={{ py: 4 }}>
                      No attack launched. Select equipment to launch an attack.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                attacks.map(attack => (
                  <TableRow key={attack.id}>
                    <TableCell>
                      <Typography fontWeight="bold">{attack.name}</Typography>
                    </TableCell>
                    <TableCell>{attack.equipment_instance?.ip_address || 'N/A'}</TableCell>
                    <TableCell>
                      <Chip label={attack.attack_vector} size="small" />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={attack.status}
                        size="small"
                        sx={{ bgcolor: STATUS_COLORS[attack.status], color: 'white' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={attack.complexity}
                        size="small"
                        sx={{ bgcolor: CRITICALITY_COLORS[attack.complexity], color: 'white' }}
                      />
                    </TableCell>
                    <TableCell>
                      {new Date(attack.created_at).toLocaleString()}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* Auto-Detect Dialog */}
      <Dialog open={autoDetectDialog} onClose={() => setAutoDetectDialog(false)}>
        <DialogTitle>Automatic Detection</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter the scan ID to automatically detect equipment.
          </Typography>
          <TextField
            fullWidth
            label="ID du scan"
            value={selectedScanId}
            onChange={(e) => setSelectedScanId(e.target.value)}
            placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAutoDetectDialog(false)}>Annuler</Button>
          <Button onClick={handleAutoDetect} variant="contained" disabled={!selectedScanId}>
            Detect
          </Button>
        </DialogActions>
      </Dialog>

      {/* Attack Dialog */}
      <Dialog open={attackDialog} onClose={() => setAttackDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Lancer une Attaque - {selectedInstance?.name || selectedInstance?.ip_address}
        </DialogTitle>
        <DialogContent>
          {selectedInstance?.equipment_type?.recommended_attacks ? (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {selectedInstance.equipment_type.recommended_attacks.map((attack, i) => (
                <Grid item xs={12} sm={6} md={4} key={i}>
                  <Card
                    sx={{ cursor: 'pointer', '&:hover': { boxShadow: 3 } }}
                    onClick={() => handleLaunchAttack(typeof attack === 'string' ? attack : attack.name)}
                  >
                    <CardContent>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {typeof attack === 'string' ? attack.replace('_', ' ') : attack.name}
                      </Typography>
                      <Button size="small" color="error" startIcon={<PlayIcon />} sx={{ mt: 1 }}>
                        Lancer
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Alert severity="info">
              No attacks recommended for this equipment type.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAttackDialog(false)}>Fermer</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
